import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BadgeCheck, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Modal } from './Modal';
import { ContractDocument } from './ContractDocument';
import { api } from '../lib/api';
import { useSession } from '../lib/session';

/**
 * Reading what your job is, once, before you start doing it.
 *
 * ## Why it is a second gate rather than a third policy document
 *
 * The terms and the privacy notice are a contract with the business: identical
 * for everybody, written by a lawyer, changed when the law does. This is a
 * description of a job — different for a technician and an accountant, written
 * by whoever runs the place, and changed when the business changes how it
 * works. Bundling them would put those two on the same page, under one
 * checkbox, and an acceptance of "the terms and also whatever my duties are"
 * is a weaker record than two separate ones.
 *
 * ## Why it comes second, and why nesting is not enough to make it so
 *
 * Deliberately after the policy gate. The terms include the clause that makes
 * anything else binding, so agreeing to duties before agreeing to the terms is
 * the wrong order — and it is the order somebody expects: what the business
 * promises you, then what you owe the business.
 *
 * This component is nested inside `PolicyGate`, and nesting turns out to
 * enforce nothing. `PolicyGate` renders its children *and* its modal — the
 * application sits behind the dialog rather than being replaced by it — so this
 * component mounts while the terms are still outstanding, and the first version
 * of it put a second blocking dialog on top of the first. Two stacked gates,
 * the wrong one in front.
 *
 * So the order is asked for explicitly: this gate holds off while any policy is
 * outstanding. The cost is one extra query, already cached by the gate above.
 *
 * ## Why it blocks, and what happens when the text is empty
 *
 * It blocks for the same reason the policy gate does: an acceptance somebody
 * scrolled past is not an acceptance. But an administrator who has deliberately
 * cleared a role's text has said there is nothing to accept, and the API
 * returns nothing outstanding in that case rather than locking that role out of
 * a dispatch system over an empty document.
 *
 * If the endpoint is unreachable it lets people work. A monitoring failure must
 * not become an outage.
 */
export function ResponsibilitiesGate({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const { user, signOut } = useSession();
  const [ticked, setTicked] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * The same query key the policy gate uses, so this is served from its cache
   * rather than costing a second request.
   */
  const policies = useQuery({
    queryKey: ['policies-outstanding'],
    queryFn: () => api.outstandingPolicies(),
    enabled: Boolean(user),
  });

  const status = useQuery({
    queryKey: ['responsibilities-outstanding'],
    queryFn: () => api.outstandingResponsibilities(),
    enabled: Boolean(user),
  });

  const accept = useMutation({
    mutationFn: () => api.acceptResponsibilities(status.data!.outstanding!.version),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['responsibilities-outstanding'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  if (!user || status.isPending || status.isError) return <>{children}</>;

  /*
   * Wait for the terms. Not merely a matter of order: two blocking dialogs at
   * once means the person reads whichever the stacking context happens to put
   * in front, and the acceptance record then says they agreed to something
   * they may never have seen.
   */
  const policiesOutstanding = policies.data?.outstanding?.length ?? 0;
  if (policies.isPending || policiesOutstanding > 0) return <>{children}</>;

  const outstanding = status.data?.outstanding;
  if (!outstanding) return <>{children}</>;

  return (
    <>
      {children}

      <Modal
        wide
        title={`What is expected of you: ${outstanding.title}`}
        subtitle="Please read this once. It describes what your role is answerable for."
        footer={
          <div className="space-y-3">
            {error && (
              <p role="alert" className="rounded-xl bg-danger-50 p-3 text-sm text-danger-700">
                {error}
              </p>
            )}

            {/*
              The whole label is the tap target, not the box inside it. Ticked
              with a thumb, sometimes outdoors, on a phone.
            */}
            <label className="-m-2 flex cursor-pointer items-start gap-3 p-2 text-[15px] leading-relaxed">
              <input
                type="checkbox"
                className="mt-0.5 h-5 w-5 shrink-0"
                checked={ticked}
                onChange={(event) => setTicked(event.target.checked)}
              />
              <span>{outstanding.acceptanceLine}</span>
            </label>

            <div className="flex flex-wrap items-center justify-between gap-3">
              <button type="button" className="btn-quiet" onClick={() => void signOut()}>
                Sign out instead
              </button>

              <button
                type="button"
                className="btn-primary"
                disabled={!ticked || accept.isPending}
                onClick={() => accept.mutate()}
              >
                {accept.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <BadgeCheck className="h-4 w-4" aria-hidden="true" />
                )}
                I accept
              </button>
            </div>
          </div>
        }
      >
        {/*
          Rendered with the contract renderer, not a `<pre>`.
          It is the same shape of document — headings and bullets an
          administrator typed — and it has to be readable, because somebody is
          about to say they have read it. A wall of monospace is what people
          scroll past.
        */}
        <ContractDocument body={outstanding.body} className="text-[15px]" />
      </Modal>
    </>
  );
}
